import { RefObject, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import type { AllotmentHandle } from 'allotment'
import { computePaneSizes } from './utils'

/**
 * Resets the Allotment split to a route-specific default whenever the user
 * navigates. Uses the imperative `resize` API so the surrounding panes (and
 * the 3D scene living in the left pane) are not remounted.
 */
export const useAllotmentRouteResize = (
  containerRef: RefObject<HTMLElement>,
  allotmentRef: RefObject<AllotmentHandle>,
  desiredRightSize: number,
) => {
  const { pathname } = useLocation()

  useEffect(() => {
    const el = containerRef.current
    const api = allotmentRef.current
    if (!el || !api) return

    let raf = 0
    let attempts = 0

    const tryResize = () => {
      const total = el.clientWidth
      // Container not laid out yet — wait for the next frame.
      if (!total) {
        raf = requestAnimationFrame(tryResize)
        return
      }
      try {
        api.resize(computePaneSizes(total, desiredRightSize))
      } catch (error) {
        // Allotment registers its panes asynchronously (only after its internal
        // ResizeObserver fires), so an early `resize` call can hit an empty view
        // list and throw. Retry on the next frame until the panes exist instead
        // of spamming the console on every navigation.
        if (attempts++ < 30) {
          raf = requestAnimationFrame(tryResize)
        } else {
          // eslint-disable-next-line no-console
          console.error('Error resizing allotment', error)
        }
      }
    }

    raf = requestAnimationFrame(tryResize)

    return () => cancelAnimationFrame(raf)
  }, [pathname, desiredRightSize, containerRef, allotmentRef])
}
